/**
 * Durable localStorage-backed map machinery shared by the meeting ledgers
 * (post-processing jobs, interrupted recordings, last-finalized boundaries),
 * plus the ledger record types and stored-row schemas.
 *
 * `meetings.state.ts` owns the MobX store and the domain-facing methods; this
 * module owns the storage mechanics: schema-validated hydration, the
 * read-merge-write persist that preserves other users' rows, and the
 * case-insensitive keying every write path relies on.
 */

import { MeetingPostProcessingCompletedPayloadSchema } from '@electron/shared/meetings.types';
import { type LocalTranscriptSegment } from '@littlebird/sdk';
import { observable } from 'mobx';
import { z } from 'zod';

import { userStore } from '@/stores/user.store';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import type { RecordingSession } from './meetings.recording-session';
import type { PendingTranscript } from './transcript-sync/TranscriptSyncManager';

type DurableRow = { meetingId: string; userId?: string };

type DurableMapOptions<Row extends DurableRow> = {
  storageKey: string;
  /** Validates one stored row on hydrate; malformed rows are dropped without
   * rewriting storage (they fall out on the next persist). */
  schema: z.ZodType<Row>;
  /** User-scoped maps hydrate only the signed-in user's rows and persist with
   * a read-merge-write that preserves other users' rows in storage. */
  userScoped: boolean;
  /** Label for the persistence-failure warning, e.g. 'post-processing jobs'. */
  debugLabel: string;
  /** When set, `set` re-inserts the row at the back of the eviction order and
   * evicts the oldest rows beyond the cap. */
  maxEntries?: number;
};

/**
 * Creates a durable map keyed by lowercased meeting id. Mutations persist
 * immediately; persistence failures are tolerated (the in-memory state is
 * kept and a warning is logged). The backing map is a shallow observable so
 * MobX reactions see row additions/removals; callers must mutate from within
 * actions, which every `meetingStore` method already guarantees.
 */
export function createDurableMap<Row extends DurableRow>({
  storageKey,
  schema,
  userScoped,
  debugLabel,
  maxEntries,
}: DurableMapOptions<Row>) {
  const rows = observable.map<string, Row>([], { deep: false });

  function loadStoredRows(): Row[] {
    const storedRows = getLocalStorageItem(storageKey);
    if (!Array.isArray(storedRows)) return [];

    const parsedRows: Row[] = [];
    for (const storedRow of storedRows) {
      const parsedRow = schema.safeParse(storedRow);
      if (parsedRow.success) parsedRows.push(parsedRow.data);
    }
    return parsedRows;
  }

  /** Loads only the current user's rows when user-scoped; other users' rows
   * stay in storage untouched until they sign back in. */
  function hydrateFromStorage() {
    rows.clear();
    const currentUserId = userStore.userId;
    if (userScoped && !currentUserId) return;

    for (const storedRow of loadStoredRows()) {
      if (userScoped && storedRow.userId !== currentUserId) continue;
      const meetingId = storedRow.meetingId.toLowerCase();
      rows.set(meetingId, { ...storedRow, meetingId });
    }
  }

  function persist() {
    try {
      if (!userScoped) {
        setLocalStorageItem(storageKey, Array.from(rows.values()));
        return true;
      }

      const currentUserId = userStore.userId;
      // Without an owner we can neither stamp new rows nor safely rewrite
      // the shared array (we would wipe other users' pending rows).
      if (!currentUserId) return false;

      const otherUsersRows = loadStoredRows().filter(
        (storedRow) => storedRow.userId !== currentUserId
      );
      const currentUserRows = Array.from(rows.values()).map((row) => ({
        ...row,
        userId: currentUserId,
      }));
      const durableRows = [...otherUsersRows, ...currentUserRows];
      if (durableRows.length === 0) {
        localStorage.removeItem(storageKey);
        return true;
      }

      setLocalStorageItem(storageKey, durableRows);
      return true;
    } catch (error) {
      console.warn(`[meetings] Failed to persist ${debugLabel}`, error);
      return false;
    }
  }

  /** Stores the row under its lowercased meeting id and persists. */
  function set(row: Row) {
    const meetingId = row.meetingId.toLowerCase();
    if (maxEntries !== undefined) {
      // Re-insert so the row moves to the back of the eviction order
      rows.delete(meetingId);
    }
    rows.set(meetingId, { ...row, meetingId });
    if (maxEntries !== undefined) {
      while (rows.size > maxEntries) {
        const oldestMeetingId = rows.keys().next().value;
        if (oldestMeetingId === undefined) break;
        rows.delete(oldestMeetingId);
      }
    }
    return persist();
  }

  /** Case-insensitive read matching the lowercased write paths. */
  function get(meetingId: string): Row | undefined {
    return rows.get(meetingId.toLowerCase());
  }

  /** Removes the row and persists; returns whether the row existed. */
  function remove(meetingId: string): boolean {
    const existed = rows.delete(meetingId.toLowerCase());
    persist();
    return existed;
  }

  /** Logout hygiene: empties the in-memory map without touching storage. */
  function clearInMemory() {
    rows.clear();
  }

  return {
    rows,
    hydrateFromStorage,
    persist,
    set,
    get,
    remove,
    clearInMemory,
  };
}

// --- Post-processing jobs ledger: record types + stored-row schema ---

/** A round whose replace-suffix finalization failed retryably and is now parked:
 * the completed segments plus the round watermark/epoch/expectedLength are
 * retained so the same idempotent replace can be re-attempted. Audio stays
 * un-acked while parked. */
export type MeetingPostProcessingParkedState = {
  state: 'failed';
  meetingId: string;
  completed: { transcript: { segments: LocalTranscriptSegment[] } };
  /** Round watermark (see the running state's `replaceFromOffset`). */
  replaceFromOffset: number | null;
  sessionEpoch?: number;
  processingToken?: string;
  expectedLength?: number;
  /** This stop deferred the first summary until the user requests it. */
  deferSummary?: boolean;
  /** Set once the final transcript is safe to summarize manually. */
  summaryDeferredReadyAt?: string;
  failed: {
    errorCode: 'backend_upload_failed';
    message: string;
  };
};

export type MeetingPostProcessingRunningState = {
  state: 'running';
  meetingId: string;
  startedAt: string;
  /** Round watermark: code-point offset where this round's replaceable live
   * tail begins. Null = watermark unknown, so finalization keeps the live
   * transcript rather than replacing at a guessed offset. Legacy records
   * persisted without the field hydrate to null. */
  replaceFromOffset: number | null;
  /** Increments every time a session for this meeting stops; echoed on the
   * post-processing start payload so a stale round's terminal event can be
   * discarded. */
  sessionEpoch?: number;
  processingToken?: string;
  releasePending?: boolean;
  finalized?: {
    boundary: number | null;
    kind: 'replaced' | 'kept_live';
    summaryGenerated: boolean;
  };
  /** Code-point backend length observed after the final flush at stop; Task 5
   * sends it as the replace call's concurrent-writer guard. */
  expectedLength?: number;
  /** This stop deferred the first summary until the user requests it. */
  deferSummary?: boolean;
  /** Set when a retryable failure leaves the live transcript ready for a
   * manually requested summary while the post-processing round stays open. */
  summaryDeferredReadyAt?: string;
  /** Set when a retryable native post-processing failure already summarized the
   * intact live transcript. Keeps the record un-acked (audio survives for
   * reprocessing) while making the re-delivered failure idempotent: reconcile
   * must not re-ingest/re-summarize the same round in a loop. */
  liveSummarizedAt?: string;
};

/** Ledger fields captured when a round's `running` record is created at stop. */
export type RunningPostProcessingLedgerFields = {
  replaceFromOffset: number | null;
  sessionEpoch: number;
  processingToken?: string;
  expectedLength: number;
  deferSummary?: boolean;
};

/** The ledger stores exactly two round states: running and parked. */
export type MeetingPostProcessingJobSourceState =
  | MeetingPostProcessingParkedState
  | MeetingPostProcessingRunningState;

export type MeetingPostProcessingJobState =
  MeetingPostProcessingJobSourceState & {
    userId?: string;
  };

const StoredPostProcessingJobBaseSchema = z.object({
  meetingId: z.string(),
  userId: z.string(),
  // Tolerated so records written by the retired parked retry budget still
  // hydrate; the ledger no longer writes it
  finalizationAttemptCount: z.number().optional(),
});

// Records persisted before the ledger carried a watermark hydrate it to null
// (unknown) exactly once, here; everything downstream sees `number | null`.
const StoredReplaceFromOffsetSchema = z
  .number()
  .nullable()
  .optional()
  .transform((replaceFromOffset) => replaceFromOffset ?? null);

// Jobs survive restarts so recovery can pick them up: running jobs re-attach
// their terminal-event handler on reconcile, while parked backend-upload
// failures retain the completed transcript for upload retries.
export const StoredPostProcessingJobSchema = z.discriminatedUnion('state', [
  StoredPostProcessingJobBaseSchema.extend({
    state: z.literal('running'),
    startedAt: z.string(),
    replaceFromOffset: StoredReplaceFromOffsetSchema,
    sessionEpoch: z.number().optional(),
    processingToken: z.string().uuid().optional(),
    releasePending: z.boolean().optional(),
    finalized: z
      .object({
        boundary: z.number().nullable(),
        kind: z.enum(['replaced', 'kept_live']),
        summaryGenerated: z.boolean(),
      })
      .optional(),
    expectedLength: z.number().optional(),
    deferSummary: z.boolean().optional(),
    summaryDeferredReadyAt: z.string().optional(),
    liveSummarizedAt: z.string().optional(),
  }),
  StoredPostProcessingJobBaseSchema.extend({
    state: z.literal('failed'),
    completed: MeetingPostProcessingCompletedPayloadSchema,
    replaceFromOffset: StoredReplaceFromOffsetSchema,
    sessionEpoch: z.number().optional(),
    processingToken: z.string().uuid().optional(),
    expectedLength: z.number().optional(),
    deferSummary: z.boolean().optional(),
    summaryDeferredReadyAt: z.string().optional(),
    failed: z.object({
      errorCode: z.literal('backend_upload_failed'),
      message: z.string(),
    }),
  }),
]);

// --- Interrupted recordings: record type + stored-row schema ---

export type InterruptedRecordingRecord = {
  meetingId: string;
  userId: string;
  /** ISO timestamp the grace period anchors to. */
  interruptedAt: string;
  reason:
    | 'contextkit-crash'
    | 'post-processing-start-failure'
    | 'transcript-sync-failure'
    | 'transcript-revision-mismatch'
    | 'stop-pending';
  recordingSession?: RecordingSession;
  finalizationStarted?: boolean;
  /** A summary request that has not succeeded yet; the next promotion attempt sends it again. */
  summaryPending?: boolean;
  transcriptRevision?: string | null;
  processingToken?: string;
  pendingTranscript?: PendingTranscript;
  syncedLength?: number;
  usesLocalPostProcessing: boolean;
  /** Failed promotions increment this; the scheduler retries with a delay. */
  promotionAttempts?: number;
  /** Code-point boundary to replace; null or absent means keep the live transcript. */
  replaceWatermark?: number | null;
  /** Carries an explicit-stop summary decision through a native start failure. */
  deferSummary?: boolean;
};

export const StoredInterruptedRecordingSchema = z.object({
  meetingId: z.string(),
  userId: z.string(),
  interruptedAt: z.string(),
  recordingSession: z
    .object({
      meetingId: z.string(),
      deviceId: z.string(),
      sessionId: z.string(),
    })
    .optional(),
  finalizationStarted: z.boolean().optional(),
  summaryPending: z.boolean().optional(),
  transcriptRevision: z.string().nullable().optional(),
  processingToken: z.string().uuid().optional(),
  reason: z.enum([
    'contextkit-crash',
    'post-processing-start-failure',
    'transcript-sync-failure',
    'transcript-revision-mismatch',
    'stop-pending',
  ]),
  pendingTranscript: z
    .object({
      currentOffset: z.number(),
      deltas: z.array(z.object({ speaker: z.string(), text: z.string() })),
      retryBatch: z
        .object({ speaker: z.string(), text: z.string() })
        .optional(),
    })
    .optional(),
  syncedLength: z.number().optional(),
  usesLocalPostProcessing: z.boolean(),
  promotionAttempts: z.number().optional(),
  replaceWatermark: z.number().nullable().optional(),
  deferSummary: z.boolean().optional(),
});

// --- Last-finalized boundaries: stored-row schema ---

/** Where the last acked (finalized) round left the backend transcript, in
 * code points. Null = finalized but the settled length is unknown (a
 * null-watermark keep-live, e.g. a background promotion) — a later
 * crash-resume must then keep-live rather than replace at a guessed offset. */
export const StoredFinalizedBoundarySchema = z.object({
  meetingId: z.string(),
  boundary: z.number().nullable(),
});
